import TripPriceClass from '../TripPriceClass';
import BusBillHandler from './BusBillHandler';
import { type PassengerFare } from '@itinerary/types';
import VerifyResult from '@itinerary/VerifyResult';
import PassengerForm from '@itinerary/PassengerForm';
import { PassengerFields } from '@store/form';
import { type InvoiceEntry } from '@store/invoice';
import { PaxType } from '@utils/types';
import { ReserveType } from '@utils/itinerary';

import {
    CreateReserveApiApiCreateReserveGet,
    type GetServicesResultBus,
} from '@clients/BusApi';
import type PriceClassExtraOption from '@itinerary/PriceClassExtraOption';

export default class BusPriceClass extends TripPriceClass {
    _netPrice = 0;

    override totalPayableForCheck: number | null = null;

    override totalPriceForCheck: number | null = null;

    override capacity = 0;

    override showCapacity = true;

    _discount = 0;

    override capacityText = '';

    override loadableTermsUrl: string | null = null;

    override passengerFares: PassengerFare[] = [];

    isAvailable = false;

    override cancellationTerms: string | null = '';

    id = 0;

    override title = '';

    private readonly reserveType: ReserveType = ReserveType.Bus;

    override get prePriceDiscountPercent() {
        const serveType = toValue(this.servicesPreDiscountPercent);
        return this.reserveType === ReserveType.Bus
            ? serveType.Bus
            : serveType.Taxi;
    }

    override descriptionItems: string[] = [];

    exclusiveOption = false;

    totallyExclusive = false;

    override getAvailableExtraOptions(): PriceClassExtraOption[] {
        let price = this.singlePayable;
        if (this.exclusiveOption) {
            price = this.capacity * this.singlePayable;
        }
        return this.exclusiveOption || this.totallyExclusive
            ? [
                  {
                      id: 'exclusive',
                      title: 'سرویس دربستی',
                      description:
                          'رزرو کل سرویس به‌صورت دربستی (حداکثر ظرفیت ۳ مسافر)',
                      price,
                      disabled: this.totallyExclusive,
                      default: this.totallyExclusive,
                  },
              ]
            : [];
    }

    get isSelectedToBeExclusive(): boolean {
        return this.selectedExtraOptions.some(o => o.id === 'exclusive');
    }

    get singlePrice() {
        return this.singlePayable + this.discount;
    }

    override get netPrice() {
        return this.haveCustomDiscount
            ? (this._netPrice ?? 0) - (this._discount ?? 0)
            : this._netPrice ?? 0;
    }

    get discount() {
        return this.haveCustomDiscount
            ? this.getPreDiscountAmount(
                  (this._netPrice ?? 0) - (this._discount ?? 0)
              )
            : this._discount ?? 0;
    }

    constructor(bus?: GetServicesResultBus, reserveType?: ReserveType) {
        super();

        if (!bus) {
            return;
        }
        this.reserveType = reserveType ?? ReserveType.Bus;
        const netCustomDiscountAmount = (bus.price ?? 0) - (bus.discount ?? 0);
        this._netPrice = bus.price ?? 0;
        this._discount = bus.discount ?? 0;
        this.capacity = bus.capacity ?? 0;
        this.capacityText = bus.capacity?.toString() ?? '';
        this.capacityTextReplacement = bus.isExclusive ? 'سرویس دربستی' : null;
        this.totallyExclusive = !!bus.isExclusive;
        this.passengerFares = [
            {
                paxType: PaxType.Seat,
                price: this.haveCustomDiscount
                    ? this.getPriceWithPreDiscount(netCustomDiscountAmount)
                    : bus.price ?? 0,
                discount: this.haveCustomDiscount
                    ? this.getPreDiscountAmount(netCustomDiscountAmount)
                    : bus.discount ?? 0,
            },
        ];

        this.isAvailable = (bus.capacity ?? 0) > 0;
        this.cancellationTerms = bus.penaltyText ?? '';
        this.id = bus.id ?? 0;
        this.title = bus.busType ?? '';
        this.descriptionItems = [];
        this.showCapacity = true;
        this.exclusiveOption =
            !!bus.needsSelectSeat && !!bus.isCar && !!bus.hasNoPassengers;
        if (bus.isExclusive) {
            this.selectedExtraOptions = this.getAvailableExtraOptions();
        }
    }

    override createPassengerForms(): {
        adultForm: PassengerForm;
        childForm: PassengerForm;
        infantForm: PassengerForm;
        exclusiveForm: PassengerForm;
    } {
        const defaultFields =
            PassengerFields.PersianFirstName |
            PassengerFields.PersianLastName |
            PassengerFields.NationalCode |
            PassengerFields.Gender |
            PassengerFields.BirthDate;

        const adultForm: PassengerForm = new PassengerForm({
            title: 'مسافر',
            type: PaxType.Adult,
            defaultFields,
            iranianExtraFields: PassengerFields.None,
            foreignerExtraFields: PassengerFields.None,
            ageLimit: {
                adult: 0,
                child: 0,
            },
        });

        const exclusiveForm = new PassengerForm({
            type: PaxType.Adult,
            title: 'سرپرست مسافرین',
            defaultFields,
            iranianExtraFields: PassengerFields.None,
            foreignerExtraFields: PassengerFields.None,
            ageLimit: {
                adult: 0,
                child: 0,
            },
        });

        return {
            adultForm,
            childForm: adultForm,
            infantForm: adultForm,
            exclusiveForm,
        };
    }

    override async verify(
        count: { adultCount: number; childCount: number; infantCount: number },
        billId: number | null
    ): Promise<VerifyResult> {
        const realCount = count.adultCount + count.childCount;
        let paxCount = realCount;
        if (this.totallyExclusive) {
            paxCount = 1;
        } else if (this.isSelectedToBeExclusive) {
            paxCount = this.capacity;
        }
        const { data } = await CreateReserveApiApiCreateReserveGet({
            busId: this.id,
            count: paxCount,
            billId: billId ?? undefined,
        });

        const prices: InvoiceEntry[] = [];
        const netCustomDiscountPrice = (data.price ?? 0) - (data.discount ?? 0);
        const price = this.haveCustomDiscount
            ? netCustomDiscountPrice
            : data.price ?? 0;
        const discount = this.haveCustomDiscount
            ? this.getPreDiscountAmount(netCustomDiscountPrice)
            : data.discount;
        const totalPrice = price + (data.discount ?? 0);
        if (this.totallyExclusive) {
            prices.push({
                paxType: PaxType.Adult,
                count: 1,
                price: totalPrice,
                discount,
                title: 'دربستی',
            });
        } else {
            prices.push({
                paxType: PaxType.Seat,
                count: realCount,
                price: totalPrice / paxCount,
                discount: (discount ?? 0) / count.adultCount,
                title: '',
            });
            if (this.isSelectedToBeExclusive) {
                prices.push({
                    paxType: PaxType.EmptySeat,
                    count: paxCount - realCount,
                    price: totalPrice / paxCount,
                    discount: (discount ?? 0) / count.adultCount,
                    title: '',
                });
            }
        }

        const { adultForm, exclusiveForm } = this.createPassengerForms();
        return new VerifyResult(
            new BusBillHandler(
                data.reserveID ?? 0,
                this.id,
                this.totallyExclusive
            ),
            {
                haveCustomDiscount: !!this.prePriceDiscountPercent,
                paxList: this.totallyExclusive
                    ? [exclusiveForm.clone()]
                    : [
                          ...Array(realCount)
                              .fill(0)
                              .map(() => adultForm.clone()),
                      ],
                billCode: data.billCode ?? undefined,
                adultForm,
                billId: data.billID,
                payablePrice: price,
                discount,
                totalPrice,
                remainingTime: this.remainingTimeCalculator({
                    passengersCount: realCount,
                }),
                priceEntries: [
                    {
                        prices,
                    },
                ],
                reserveId: data.reserveID,
                needsDestinationMobile: false,
                maxCapacity: data.maxAvailableSeats,
                disablePaxTypeValidation: true,
            }
        );
    }

    override loadPreInvoice(count: {
        adultCount: number;
        childCount: number;
        infantCount: number;
    }): Promise<InvoiceEntry[]> {
        return Promise.resolve([
            {
                paxType: PaxType.Seat,
                price: this.singlePayable,
                discount: this.discount,
                count: this.totallyExclusive ? 1 : count.adultCount,
                title: this.totallyExclusive ? 'دربستی' : undefined,
            },
            ...(this.isSelectedToBeExclusive &&
            this.capacity - count.adultCount > 0 &&
            !this.totallyExclusive
                ? [
                      {
                          paxType: PaxType.EmptySeat,
                          price: this.singlePayable,
                          discount: this.discount,
                          count: this.capacity - count.adultCount,
                      },
                  ]
                : []),
        ]);
    }
}
